// Promociones del POS (catálogo). POS → Configuración → Promociones.
// Tipos: NxM, combo, por monto, regalo. Se aplican automáticamente en la venta
// (promos_evaluar) y pueden acotarse por franja horaria, días y local (426):
// el "happy hour" que exhibe la pantalla es lo que cobra la caja.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Campo, DIAS_SEMANA_MIN, Modal, Promocion, PromocionItem, ProductoMin, Switch, TIPOS_PROMO, fieldCls, locationsList, posRpc, productosMinList } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

// La promoción llega con sus componentes embebidos (RPC pos_promociones)
type PromoRow = Promocion & { items: PromocionItem[] };

const vacio = {
  codigo: '', nombre: '', tipo: 'nxm', vigente_desde: '', vigente_hasta: '', prioridad: 0, es_activo: true,
  producto_id: '', nxm_n: 2, nxm_m: 1, cantidad_min: 1, regalo_producto_id: '', regalo_cantidad: 1,
  monto_min: 0, monto_pct: 0, combo_precio: 0,
  hora_inicio: '', hora_fin: '', dias_semana: [] as number[], location_id: '',
};
type Form = typeof vacio;
const tipoLabel = (t: string) => TIPOS_PROMO.find((x) => x.v === t)?.label ?? t;
const hhmm = (h: string | null) => (h ? h.slice(0, 5) : '');

// Resumen corto de la franja para la lista ("14:00–16:00 · L,M,X · Local Aurora")
function franjaResumen(p: Promocion, nombreLocal: (id: string | null) => string | null): string | null {
  const partes: string[] = [];
  if (p.hora_inicio && p.hora_fin) partes.push(`${hhmm(p.hora_inicio)}–${hhmm(p.hora_fin)}`);
  if (p.dias_semana?.length) partes.push(p.dias_semana.map((d) => DIAS_SEMANA_MIN[d] ?? '?').join(','));
  const loc = nombreLocal(p.location_id);
  if (loc) partes.push(loc);
  return partes.length ? partes.join(' · ') : null;
}

export function Promociones() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<PromoRow[]>({ queryKey: ['pos-promos'], queryFn: () => posRpc<PromoRow[]>('pos_promociones') });
  const { data: productos = [] } = useQuery<ProductoMin[]>({ queryKey: ['pos-prod-min'], queryFn: productosMinList });
  const { data: locales = [] } = useQuery({ queryKey: ['pos-locations'], queryFn: locationsList });
  const nombreLocal = (id: string | null) => (id ? (locales.find((l) => l.id === id)?.nombre ?? '…') : null);

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(vacio);
  const [items, setItems] = useState<PromocionItem[]>([]);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (p?: PromoRow) => {
    if (p) {
      setEditId(p.id);
      setForm({
        codigo: p.codigo ?? '', nombre: p.nombre, tipo: p.tipo, vigente_desde: p.vigente_desde ?? '', vigente_hasta: p.vigente_hasta ?? '',
        prioridad: p.prioridad, es_activo: p.es_activo, producto_id: p.producto_id ?? '', nxm_n: p.nxm_n ?? 2, nxm_m: p.nxm_m ?? 1,
        cantidad_min: p.cantidad_min ?? 1, regalo_producto_id: p.regalo_producto_id ?? '', regalo_cantidad: p.regalo_cantidad ?? 1,
        monto_min: p.monto_min ?? 0, monto_pct: p.monto_pct ?? 0, combo_precio: p.combo_precio ?? 0,
        hora_inicio: hhmm(p.hora_inicio), hora_fin: hhmm(p.hora_fin), dias_semana: p.dias_semana ?? [], location_id: p.location_id ?? '',
      });
      setItems((p.items ?? []).map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })));
    } else { setEditId(null); setForm(vacio); setItems([]); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const t = form.tipo;
      const data = {
        id: editId || undefined,
        codigo: form.codigo || null, nombre: form.nombre, tipo: t,
        vigente_desde: form.vigente_desde || null, vigente_hasta: form.vigente_hasta || null,
        prioridad: Number(form.prioridad) || 0, es_activo: form.es_activo,
        producto_id: (t === 'nxm' || t === 'regalo') ? (form.producto_id || null) : null,
        nxm_n: t === 'nxm' ? Number(form.nxm_n) : null, nxm_m: t === 'nxm' ? Number(form.nxm_m) : null,
        cantidad_min: t === 'regalo' ? Number(form.cantidad_min) : null,
        regalo_producto_id: t === 'regalo' ? (form.regalo_producto_id || null) : null,
        regalo_cantidad: t === 'regalo' ? Number(form.regalo_cantidad) : null,
        monto_min: t === 'monto' ? Number(form.monto_min) : null, monto_pct: t === 'monto' ? Number(form.monto_pct) : null,
        combo_precio: t === 'combo' ? Number(form.combo_precio) : null,
        hora_inicio: form.hora_inicio || null, hora_fin: form.hora_fin || null,
        dias_semana: form.dias_semana.length ? form.dias_semana : null,
        location_id: form.location_id || null,
        items: t === 'combo' ? items.filter((x) => x.producto_id && Number(x.cantidad) > 0).map((x) => ({ producto_id: x.producto_id, cantidad: Number(x.cantidad) })) : [],
      };
      await posRpc('pos_promocion_guardar', { _data: data });
    },
    onSuccess: () => { toast.success('Promoción guardada'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['pos-promos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => posRpc('pos_promocion_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Promoción eliminada'); void qc.invalidateQueries({ queryKey: ['pos-promos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Tag className="w-5 h-5" /> Promociones</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
      </div>
      <p className="text-xs text-muted-foreground">Las promociones se aplican solas en la venta. Con franja horaria, días o local, corren únicamente en esa ventana — y la cartelera las exhibe/oculta en sincronía con lo que cobra la caja.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Promociones ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Tipo</th><th className="py-2 pr-3">Vigencia</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin promociones</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{p.codigo ?? '—'}</td>
                  <td className="py-2 pr-3">{p.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{tipoLabel(p.tipo)}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground text-xs">
                    {p.vigente_desde ?? '—'} → {p.vigente_hasta ?? '—'}
                    {franjaResumen(p, nombreLocal) && <div className="text-amber-600 dark:text-amber-400">⏱ {franjaResumen(p, nombreLocal)}</div>}
                  </td>
                  <td className="py-2 pr-3">{p.es_activo ? 'Activa' : 'Inactiva'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${p.nombre}"?`, { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar promoción' : 'Nueva promoción'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
              <Campo label="Tipo">
                <select className={fieldCls} value={form.tipo} onChange={(e) => set({ tipo: e.target.value })}>
                  {TIPOS_PROMO.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Vigente desde"><input type="date" className={fieldCls} value={form.vigente_desde} onChange={(e) => set({ vigente_desde: e.target.value })} /></Campo>
              <Campo label="Vigente hasta"><input type="date" className={fieldCls} value={form.vigente_hasta} onChange={(e) => set({ vigente_hasta: e.target.value })} /></Campo>
              <Campo label="Prioridad"><input type="number" className={fieldCls} value={form.prioridad} onChange={(e) => set({ prioridad: Number(e.target.value) })} /></Campo>
            </div>

            {/* Franja: la promo corre solo en esta ventana/días/local (vacío = siempre/todos) */}
            <div className="rounded-md border p-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Franja (opcional) — p. ej. happy hour: solo en esta ventana la cobra la caja y la exhibe la pantalla</p>
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Desde (hora)"><input type="time" className={fieldCls} value={form.hora_inicio} onChange={(e) => set({ hora_inicio: e.target.value })} /></Campo>
                <Campo label="Hasta (hora)"><input type="time" className={fieldCls} value={form.hora_fin} onChange={(e) => set({ hora_fin: e.target.value })} /></Campo>
                <Campo label="Local">
                  <select className={fieldCls} value={form.location_id} onChange={(e) => set({ location_id: e.target.value })}>
                    <option value="">Todos</option>{locales.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                  </select>
                </Campo>
              </div>
              <Campo label="Días de la semana (ninguno = todos)">
                <div className="flex gap-1">
                  {DIAS_SEMANA_MIN.map((d, i) => {
                    const on = form.dias_semana.includes(i);
                    return (
                      <button key={i} type="button"
                        className={`h-8 w-8 rounded-md border text-xs font-semibold ${on ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
                        onClick={() => set({ dias_semana: on ? form.dias_semana.filter((x) => x !== i) : [...form.dias_semana, i].sort((a, b) => a - b) })}>
                        {d}
                      </button>
                    );
                  })}
                </div>
              </Campo>
              {form.hora_inicio && form.hora_fin && form.hora_inicio > form.hora_fin && (
                <p className="text-xs text-amber-600 dark:text-amber-400">La ventana cruza la medianoche ({form.hora_inicio} → {form.hora_fin} del día siguiente); la madrugada cuenta como el día en que abrió.</p>
              )}
            </div>

            {form.tipo === 'nxm' && (
              <div className="grid grid-cols-3 gap-3 rounded-md border p-3">
                <Campo label="Producto" className="col-span-3">
                  <select className={fieldCls} value={form.producto_id} onChange={(e) => set({ producto_id: e.target.value })}>
                    <option value="">— Selecciona —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Lleva (N)"><input type="number" min="1" className={fieldCls} value={form.nxm_n} onChange={(e) => set({ nxm_n: Number(e.target.value) })} /></Campo>
                <Campo label="Paga (M)"><input type="number" min="1" className={fieldCls} value={form.nxm_m} onChange={(e) => set({ nxm_m: Number(e.target.value) })} /></Campo>
              </div>
            )}

            {form.tipo === 'regalo' && (
              <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
                <Campo label="Producto disparador" className="col-span-2">
                  <select className={fieldCls} value={form.producto_id} onChange={(e) => set({ producto_id: e.target.value })}>
                    <option value="">— Selecciona —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Cantidad mínima"><input type="number" min="1" step="0.01" className={fieldCls} value={form.cantidad_min} onChange={(e) => set({ cantidad_min: Number(e.target.value) })} /></Campo>
                <div />
                <Campo label="Producto de regalo">
                  <select className={fieldCls} value={form.regalo_producto_id} onChange={(e) => set({ regalo_producto_id: e.target.value })}>
                    <option value="">— Selecciona —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                  </select>
                </Campo>
                <Campo label="Cantidad regalo"><input type="number" min="1" step="0.01" className={fieldCls} value={form.regalo_cantidad} onChange={(e) => set({ regalo_cantidad: Number(e.target.value) })} /></Campo>
              </div>
            )}

            {form.tipo === 'monto' && (
              <div className="grid grid-cols-2 gap-3 rounded-md border p-3">
                <Campo label="Monto mínimo de compra"><input type="number" step="0.01" className={fieldCls} value={form.monto_min} onChange={(e) => set({ monto_min: Number(e.target.value) })} /></Campo>
                <Campo label="Descuento %"><input type="number" step="0.01" className={fieldCls} value={form.monto_pct} onChange={(e) => set({ monto_pct: Number(e.target.value) })} /></Campo>
              </div>
            )}

            {form.tipo === 'combo' && (
              <div className="rounded-md border p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <Campo label="Precio del combo"><input type="number" step="0.01" className={`${fieldCls} w-40`} value={form.combo_precio} onChange={(e) => set({ combo_precio: Number(e.target.value) })} /></Campo>
                  <Button variant="outline" size="sm" onClick={() => setItems((it) => [...it, { producto_id: '', cantidad: 1 }])}><Plus className="w-4 h-4 mr-1" /> Componente</Button>
                </div>
                {items.map((it, i) => (
                  <div key={i} className="flex gap-2 items-end">
                    <select className={fieldCls} value={it.producto_id} onChange={(e) => setItems((arr) => arr.map((x, j) => j === i ? { ...x, producto_id: e.target.value } : x))}>
                      <option value="">— Producto —</option>{productos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                    <input type="number" min="1" step="0.01" className={`${fieldCls} w-24 text-right`} value={it.cantidad} onChange={(e) => setItems((arr) => arr.map((x, j) => j === i ? { ...x, cantidad: Number(e.target.value) } : x))} />
                    <button className="h-10 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-2" onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground">Agrega los productos que componen el combo.</p>}
              </div>
            )}

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activa" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
